import * as React from 'react'

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../lib/ui/form'
import { RadioGroup, RadioGroupItem } from '../../lib/ui/radio-group'
import { cn } from '../../lib/utils'
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Star } from '../icons/YnsIcon'

export function RatingField({
  name,
  label,
  choices,
  control,
  programSurvey = false,
  yesOrNo = false,
  oneToFive = false,
  star = false,
  className,
}) {
  const fieldId = React.useId()

  const checkedStyles =
    programSurvey && !star
      ? 'has-[:checked]:bg-primary has-[:checked]:text-primary-foreground'
      : !programSurvey && !star
        ? 'has-[:checked]:border-primary has-[:checked]:bg-accent has-[:checked]:text-accent-foreground'
        : ''

  const starCheckedStyles = star
    ? ''
    : 'px-3 border hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground has-[:focus-visible]:outline-none has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2'

  const [hoveredValue, setHoveredValue] = React.useState(null)

  const toNumberOrNull = (value) => {
    const parsed = Number(value)

    return Number.isNaN(parsed) ? null : parsed
  }

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const descriptionId = `${fieldId}-form-item-description`
        const errorId = `${fieldId}-error`
        const describedBy = fieldState.error
          ? `${descriptionId} ${errorId}`
          : descriptionId

        return (
          <FormItem className={cn('', className)}>
            <FormLabel id={`${fieldId}-label`}>{label}</FormLabel>
            {yesOrNo ? (
              <FormDescription
                id={descriptionId}
                className="mt-4 text-sm text-[#5d5d5d]"
              >
                Select Yes or No
              </FormDescription>
            ) : oneToFive ? (
              <FormDescription
                id={descriptionId}
                className="mt-4 text-sm text-[#5d5d5d]"
              >
                Rate from 1 (Not satisfied) to 5 (Very satisfied)
              </FormDescription>
            ) : (
              <FormDescription
                id={descriptionId}
                className="mt-4 text-sm text-[#5d5d5d]"
              >
                Select a rating
              </FormDescription>
            )}
            <div className={programSurvey ? ' !mt-12' : ''}>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  value={field.value}
                  name={field.name}
                  aria-labelledby={`${fieldId}-label`}
                  aria-describedby={describedBy}
                  aria-invalid={fieldState.invalid}
                  className="flex flex-row rounded-md ring-offset-background transition-colors"
                >
                  {choices.map((choice, idx) => (
                    <div
                      className="items-left flex-grow space-x-3 space-y-0"
                      key={choice.value}
                    >
                      <label
                        htmlFor={`${fieldId}-${idx}-input`}
                        className={cn(
                          `flex cursor-pointer items-center justify-center rounded-md border-[#b0b0b0] py-2 text-muted-foreground`,
                          checkedStyles,
                          starCheckedStyles,
                        )}
                        onMouseEnter={() =>
                          star && setHoveredValue(choice.value)
                        }
                        onMouseLeave={() => star && setHoveredValue(null)}
                      >
                        <RadioGroupItem
                          id={`${fieldId}-${idx}-input`}
                          className="sr-only"
                          value={choice.value}
                          aria-label={star ? choice.label : null}
                          aria-labelledby={
                            star ? null : `${fieldId}-${idx}-label`
                          }
                          aria-describedby={describedBy}
                          onFocus={() => star && setHoveredValue(choice.value)}
                          onBlur={() => star && setHoveredValue(null)}
                        />
                        {star && (
                          <>
                            <Star
                              className="star h-9 w-9"
                              aria-hidden="true"
                              fill={
                                ((hoveredValue !== null
                                  ? toNumberOrNull(hoveredValue)
                                  : toNumberOrNull(field.value)) ?? 0) >=
                                (toNumberOrNull(choice.value) ?? 0)
                                  ? '#f59e0b'
                                  : '#d4d4d4'
                              }
                              stroke={
                                ((hoveredValue !== null
                                  ? toNumberOrNull(hoveredValue)
                                  : toNumberOrNull(field.value)) ?? 0) >=
                                (toNumberOrNull(choice.value) ?? 0)
                                  ? '#6d6d6d'
                                  : ''
                              }
                            />
                          </>
                        )}
                        {yesOrNo &&
                          (choice.value === true ||
                            choice.value === 'true') && (
                            <CheckIcon
                              className="mr-4 h-6 w-6"
                              aria-hidden="true"
                            />
                          )}
                        {yesOrNo &&
                          (choice.value === false ||
                            choice.value === 'false') && (
                            <XMarkIcon
                              className="mr-4 h-6 w-6"
                              aria-hidden="true"
                            />
                          )}
                        {!star && (
                          <span
                            className="ml-0 flex flex-row items-center text-center text-sm font-medium"
                            id={`${fieldId}-${idx}-label`}
                          >
                            {choice.label}
                          </span>
                        )}
                      </label>
                    </div>
                  ))}
                </RadioGroup>
              </FormControl>
            </div>
            <FormMessage id={errorId} aria-live="polite" />
          </FormItem>
        )
      }}
    />
  )
}
